import {ChangeDetectionStrategy, Component, EffectRef, Signal, computed, effect, inject, signal, WritableSignal} from '@angular/core';
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {RouterService} from "../../../shared/services/router.service";
import {IconBarPolicy, IconBarService} from "../../../shared/services/icon-bar.service";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {NavService} from "../../../shared/services/nav.service";
import {toSignal} from '@angular/core/rxjs-interop';
import {ToasterService} from "../../../shared/services/toaster.service";
import {LanguageService} from "../../../shared/services/language.service";
import {DialogHomeLinksAllComponent} from "./dialog-home-links-all/dialog-home-links-all.component";
import {TYPE} from "../../../shared/types/types";
import {DashboardLinksStateService} from '../../../shared/services/dashboard-links-state.service';
import {SettingsMenuMode} from '../../../shared/types/generated/settingsMenuMode';

@Component({
    selector: 'app-bar-icons',
    imports: [
        FormButtonComponent,
        TooltipComponent,
        TranslatePipe,
        DialogHomeLinksAllComponent
    ],
    templateUrl: './bar-icons.component.html',
    styleUrl: './bar-icons.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BarIconsComponent {
  public readonly routerService: RouterService = inject(RouterService);
  private readonly barIconService: IconBarService = inject(IconBarService);
  private readonly toasterService: ToasterService = inject(ToasterService);
  private readonly languageService: LanguageService = inject(LanguageService);
  private readonly dashboardLinksStateService: DashboardLinksStateService = inject(DashboardLinksStateService);
  public readonly navService: NavService = inject(NavService);

  private readonly iconBarVisible: Signal<boolean> = toSignal(this.barIconService.iconBarVisible, {initialValue: false});
  private readonly iconBarPolicy: Signal<IconBarPolicy> = toSignal(this.barIconService.iconBarPolicy, {initialValue: IconBarPolicy.Toggleable});
  private readonly userMenuDisplayMode: Signal<SettingsMenuMode | null> = this.navService.userMenuDisplayMode;

  public readonly homeLinksDialogVisible: WritableSignal<boolean> = signal(false);
  private readonly iconTextCollapsed: Signal<boolean> = toSignal(this.barIconService.iconTextCollapsed, {initialValue: false});

  public readonly shouldShowHomeLinksIcon: Signal<boolean> = computed(() =>
    this.dashboardLinksStateService.links().length > 0
  );

  public readonly visibleUserMenuItems: Signal<TYPE.UserMenuItem[]> = computed(() =>
    this.navService.userMenu().filter((item) =>
      !this.navService.isLinksMenuItem(item) || this.shouldShowHomeLinksIcon()
    )
  );

  public readonly showIconText: Signal<boolean> = computed(() =>
    this.userMenuDisplayMode() === SettingsMenuMode.SIDEBAR_ICONS_AND_TEXT && !this.iconTextCollapsed()
  );

  public readonly shouldShowIconTextToggle: Signal<boolean> = computed(() =>
    this.userMenuDisplayMode() === SettingsMenuMode.SIDEBAR_ICONS_AND_TEXT
  );

  public readonly iconTextToggleIcon: Signal<string> = computed(() =>
    this.showIconText() ? 'chevron_left' : 'chevron_right'
  );

  public readonly iconTextToggleLabel: Signal<string> = computed(() =>
    this.showIconText() ? 'Collapse navigation' : 'Expand navigation'
  );

  public readonly shouldShowIconBar: Signal<boolean> = computed(() =>
    (this.userMenuDisplayMode() !== SettingsMenuMode.NO_SIDEBAR || this.iconBarPolicy() === IconBarPolicy.ForcedVisible)
    && this.visibleUserMenuItems().length > 0
    && (this.iconBarPolicy() === IconBarPolicy.ForcedVisible || this.iconBarVisible())
  );

  private readonly hasLinkMenuItem: Signal<boolean> = computed(() =>
    this.navService.userMenu().some((item) => this.navService.isLinksMenuItem(item))
  );

  private readonly loadLinksEffect: EffectRef = effect(() => {
    const loadState = this.dashboardLinksStateService.loadState();

    if (this.hasLinkMenuItem() && loadState === 'idle') {
      this.dashboardLinksStateService.loadLinks();
    }
  });

  private readonly resetIconTextCollapsedEffect: EffectRef = effect(() => {
    if (this.userMenuDisplayMode() !== SettingsMenuMode.SIDEBAR_ICONS_AND_TEXT && this.iconTextCollapsed()) {
      this.barIconService.setIconTextCollapsed(false);
    }
  });

  public onMenuItemClick(item: TYPE.UserMenuItem): void {
    if (this.navService.isLinksMenuItem(item)) {
      this.openHomeLinksDialog();
      return;
    }

    this.handleNavigation(item.route, item.name);
  }

  public openHomeLinksDialog(): void {
    this.homeLinksDialogVisible.set(true);
  }

  public toggleIconText(): void {
    if (!this.shouldShowIconTextToggle()) {
      return;
    }

    this.barIconService.setIconTextCollapsed(!this.iconTextCollapsed());
  }

  public handleNavigation(targetRoute: string, navigationLabel: string): void {
    const normalizedTargetRoute = this.normalizeRoute(targetRoute);
    const normalizedCurrentRoute = this.normalizeRoute(this.routerService.getCurrentUrl());

    if (normalizedCurrentRoute === normalizedTargetRoute) {
      this.toasterService.warning(
        navigationLabel,
        this.languageService.getTranslationByKey('ALREADYONTHISPAGE')
      );
      return;
    }

    this.routerService.navigateTo(targetRoute);
  }

  private normalizeRoute(route: string): string {
    const trimmedRoute = route.trim();
    if (!trimmedRoute) {
      return '';
    }

    if (/^https?:\/\//i.test(trimmedRoute)) {
      try {
        const parsedUrl = new URL(trimmedRoute);
        if (parsedUrl.origin === window.location.origin) {
          return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        }
      } catch {
        return trimmedRoute;
      }
    }

    const normalizedRoute = trimmedRoute.startsWith('/') ? trimmedRoute : `/${trimmedRoute}`;

    return normalizedRoute !== '/' && normalizedRoute.endsWith('/')
      ? normalizedRoute.slice(0, -1)
      : normalizedRoute;
  }
}
